import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Cpu, Play, Trash2, CheckCircle2, Database } from "lucide-react";
import { adminMlStatus, adminMlTrain, adminMlJob, adminMlModels, adminMlActivate, adminMlDelete } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { fmtApiError } from "@/lib/api";

export default function MlTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [jobId, setJobId] = useState(null);
  const pollRef = useRef(null);

  const { data: status } = useQuery({ queryKey: ["ml-status"], queryFn: adminMlStatus, refetchInterval: 8000 });
  const { data: modelsData } = useQuery({ queryKey: ["ml-models"], queryFn: adminMlModels, refetchInterval: 8000 });

  const [params, setParams] = useState({ horizon: 30, threshold_bps: 5, test_size: 0.2 });
  const [job, setJob] = useState(null);

  const trainMut = useMutation({
    mutationFn: adminMlTrain,
    onSuccess: (d) => { setJobId(d.job_id); setJob({ status: "queued", progress: 0, message: "Queued" }); },
    onError: (e) => toast({ title: "Could not start training", description: fmtApiError(e?.response?.data?.detail), variant: "destructive" }),
  });

  useEffect(() => {
    if (!jobId) return;
    pollRef.current = setInterval(async () => {
      try {
        const j = await adminMlJob(jobId);
        setJob(j);
        if (j.status === "done" || j.status === "failed") {
          clearInterval(pollRef.current);
          setJobId(null);
          qc.invalidateQueries({ queryKey: ["ml-models"] });
          qc.invalidateQueries({ queryKey: ["ml-status"] });
          toast({ title: j.status === "done" ? "Training complete" : "Training failed",
                   description: j.message, variant: j.status === "done" ? undefined : "destructive" });
        }
      } catch (_) { /* ignore */ }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [jobId, qc, toast]);

  const activateMut = useMutation({ mutationFn: adminMlActivate, onSuccess: () => { qc.invalidateQueries({ queryKey: ["ml-models"] }); qc.invalidateQueries({ queryKey: ["ml-status"] }); toast({ title: "Model activated" }); } });
  const deleteMut = useMutation({ mutationFn: adminMlDelete, onSuccess: () => { qc.invalidateQueries({ queryKey: ["ml-models"] }); toast({ title: "Model deleted" }); } });

  const models = modelsData?.models || [];
  const running = job && (job.status === "queued" || job.status === "running");

  return (
    <div className="space-y-5" data-testid="admin-ml-tab">
      {/* Data readiness */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Database} label="Candles (XAUUSD)" value={status?.candles ?? "—"} testId="ml-candles" />
        <StatCard icon={Database} label="Span (days)" value={status?.span_days ?? "—"} testId="ml-span" />
        <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid="ml-ready">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Data readiness</div>
          <div className={`text-sm font-bold mt-1 ${status?.ready ? "text-bull" : "text-amber-300"}`}>
            {status ? (status.ready ? "Ready to train" : `Need ≥ ${status.min_candles}`) : "—"}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid="ml-active">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Active model</div>
          <div className="text-sm font-bold mt-1 text-white">
            {status?.active_model ? `${status.active_model.algo} · ${status.active_model.metrics?.accuracy}` : "None"}
          </div>
        </div>
      </div>

      {/* Train */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid="ml-train-panel">
        <div className="flex items-center gap-2 mb-3">
          <Cpu className="size-4 text-amber-300" />
          <div className="text-sm font-semibold text-white">Train XGBoost + LightGBM</div>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <NumField label="Horizon (bars)" value={params.horizon} onChange={(v) => setParams({ ...params, horizon: v })} testId="ml-horizon" />
          <NumField label="Dead-band (bps)" value={params.threshold_bps} step="0.5" onChange={(v) => setParams({ ...params, threshold_bps: v })} testId="ml-threshold" />
          <NumField label="Test size" value={params.test_size} step="0.05" onChange={(v) => setParams({ ...params, test_size: v })} testId="ml-testsize" />
          <button
            data-testid="ml-train-btn"
            disabled={running || !status?.ready || trainMut.isPending}
            onClick={() => trainMut.mutate({ horizon: Number(params.horizon), threshold_bps: Number(params.threshold_bps), test_size: Number(params.test_size) })}
            className="bg-amber-400/90 text-ink-900 text-xs font-semibold rounded px-3 py-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
            {running ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-3.5" />}
            {running ? "Training…" : "Train models"}
          </button>
        </div>
        {job && (
          <div className="mt-3" data-testid="ml-job-status">
            <div className="flex justify-between text-[11px] text-white/60 mb-1">
              <span>{job.message}</span><span>{job.progress ?? 0}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className={`h-full ${job.status === "failed" ? "bg-bear" : "bg-amber-400"}`} style={{ width: `${job.progress ?? 0}%` }} />
            </div>
          </div>
        )}
        <p className="text-[11px] text-white/40 mt-3">
          Time-based split (no look-ahead leakage). Models predict {params.horizon}-bar forward direction with a {params.threshold_bps}bps dead-band.
        </p>
      </div>

      {/* Registry */}
      <div>
        <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Model registry</div>
        {models.length === 0 ? (
          <div className="text-sm text-white/50 py-4">No models yet — run a training job.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="ml-models-table">
              <thead className="text-white/40 text-left">
                <tr><th className="py-2">Algo</th><th>Version</th><th>Acc</th><th>AUC</th><th>F1</th><th>Win%</th><th>Cum%</th><th>Trades</th><th>Active</th><th></th></tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.id} className="border-t border-white/5" data-testid={`ml-model-${m.id}`}>
                    <td className="py-2.5 font-medium text-white">{m.algo}</td>
                    <td className="mono text-white/60">{m.version}</td>
                    <td>{m.metrics?.accuracy}</td>
                    <td>{m.metrics?.auc}</td>
                    <td>{m.metrics?.f1}</td>
                    <td className={Number(m.backtest?.win_rate) >= 50 ? "text-bull" : "text-bear"}>{m.backtest?.win_rate}</td>
                    <td className={Number(m.backtest?.cum_return_pct) >= 0 ? "text-bull" : "text-bear"}>{m.backtest?.cum_return_pct}</td>
                    <td>{m.backtest?.trades}</td>
                    <td>{m.active ? <CheckCircle2 className="size-4 text-bull" /> : "—"}</td>
                    <td>
                      <div className="flex gap-1.5">
                        {!m.active && (
                          <button data-testid={`ml-activate-${m.id}`} onClick={() => activateMut.mutate(m.id)}
                            className="px-2 py-1 rounded border border-bull/40 text-bull text-[10px] uppercase">Activate</button>
                        )}
                        <button data-testid={`ml-delete-${m.id}`} onClick={() => deleteMut.mutate(m.id)}
                          className="px-2 py-1 rounded border border-bear/40 text-bear"><Trash2 className="size-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1">{Icon && <Icon className="size-3" />}{label}</div>
      <div className="text-xl font-bold text-white mt-0.5">{value}</div>
    </div>
  );
}

function NumField({ label, value, step, onChange, testId }) {
  return (
    <label className="block text-xs text-white/60">
      {label}
      <input data-testid={testId} type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm text-white" />
    </label>
  );
}
